import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { User, Permission, RolePermissions, SidebarSection } from '../types';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { createLogger } from '../utils/logger';
import { getAvatarColor } from '../utils/format';
import { STORAGE_KEYS } from '../constants';
import { getProfileAvatarUrl, removeProfileAvatar, uploadProfileAvatar, validateProfileAvatar } from '../lib/profileAvatarHelpers';
import { notifyAppError } from '../utils/appEvents';

const logger = createLogger('AuthContext');

const PROFILE_CACHE_KEY = STORAGE_KEYS.PROFILE_CACHE;

function getAllowedSections(role: User['role']): SidebarSection[] {
  return role === 'PROJECT_MANAGER'
    ? ['dashboard', 'workspace', 'my-tasks', 'team', 'reports', 'planning', 'settings', 'calendar']
    : ['my-tasks', 'calendar'];
}

function mapDbUserToAppUser(data: any): User {
  return {
    id: data.id,
    name: data.name,
    email: data.email,
    role: data.role as User['role'],
    avatarColor: getAvatarColor(data.id),
    avatarUrl: getProfileAvatarUrl(data.id, data.updated_at || data.created_at),
    allowedSections: Array.isArray(data.allowed_sections) && data.allowed_sections.length > 0
      ? data.allowed_sections as SidebarSection[]
      : getAllowedSections(data.role as User['role']),
    createdAt: data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : undefined,
    notificationEmail: data.notification_email || undefined,
    notificationPhone: data.notification_phone || undefined,
    updatedAt: data.updated_at || undefined,
    password: '',
  };
}

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPasswordRecovery: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (name: string, email: string, password: string, role: string, phone: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateCurrentUserProfile: (updates: Pick<User, 'name'>) => Promise<{ success: boolean; error?: string }>;
  updateCurrentUserAvatar: (file: File) => Promise<{ success: boolean; error?: string }>;
  removeCurrentUserAvatar: () => Promise<{ success: boolean; error?: string }>;
  completePasswordRecovery: () => void;
  hasPermission: (permission: Permission) => boolean;
  isProjectManager: boolean;
  isAdmin: boolean;
  isTechnician: boolean;
  hasSidebarAccess: (section: SidebarSection) => boolean;
  // User management
  users: User[];
  addUser: (user: Omit<User, 'id'> & { password: string }) => Promise<{ success: boolean; error?: string }>;
  adminResetPassword: (userEmail: string) => Promise<{ success: boolean; error?: string }>;
  adminSetPassword: (userId: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const profileCache = useRef<Map<string, User>>(new Map());

  const persistCurrentUser = useCallback((user: User | null) => {
    setCurrentUser(user);

    if (!user) {
      sessionStorage.removeItem(PROFILE_CACHE_KEY);
      return;
    }

    profileCache.current.set(user.id, user);
    sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(user));
  }, []);

  // ==================== FETCH USER PROFILE ====================
  const fetchUserProfile = async (userId: string): Promise<User | null> => {
    // Return cached profile immediately if available
    const cached = profileCache.current.get(userId);
    if (cached) {
      logger.info('Returning cached profile:', cached.name);
      return cached;
    }

    try {
      logger.info('Fetching profile for user:', userId);

      const timeout = new Promise<null>(resolve => setTimeout(() => {
        logger.error('Profile fetch timed out');
        resolve(null);
      }, 3000));

      const fetchPromise = supabase
        .from('users')
        .select('id, name, email, role, allowed_sections, notification_email, notification_phone, created_at, updated_at')
        .eq('id', userId)
        .single();

      const result = await Promise.race([fetchPromise, timeout]);
      if (!result) return null;
      const { data, error } = result as any;

      if (error) {
        logger.error('Error fetching user profile:', error);
        return null;
      }

      if (!data) {
        logger.error('User profile not found');
        return null;
      }

      logger.info('Profile fetched successfully:', data);

      const profile = mapDbUserToAppUser(data);

      profileCache.current.set(userId, profile);
      return profile;
    } catch (error) {
      logger.error('Exception fetching user profile:', error);
      return null;
    }
  };

  // ==================== FETCH ALL USERS ====================
  const fetchUsers = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      logger.warn('Supabase not configured — cannot fetch users');
      return;
    }

    try {
      logger.info('Fetching all users...');
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, allowed_sections, notification_email, notification_phone, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading users:', error);
        return;
      }

      logger.info(`Fetched ${data?.length ?? 0} users`);
      if (data) {
        const mappedUsers: User[] = data.map(mapDbUserToAppUser);
        setUsers(mappedUsers);
      }
    } catch (error) {
      logger.error('Exception loading users:', error);
    }
  }, []);

  // Re-fetch users whenever currentUser changes (covers reload + fresh login)
  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [currentUser, fetchUsers]);

  // ==================== INITIALIZE AUTH ====================
  useEffect(() => {
    const initializeAuth = async () => {
      if (!isSupabaseConfigured()) {
        logger.warn('Supabase not configured — using mock user.');
        const mockUser: User = {
          id: 'pm-001',
          name: 'David Müller',
          email: 'david.muller@rae.com',
          role: 'PROJECT_MANAGER',
          avatarColor: '#4A90E2',
          allowedSections: getAllowedSections('PROJECT_MANAGER'),
        };
        persistCurrentUser(mockUser);
        setIsLoading(false);
        return;
      }

      logger.info('Initializing auth...');

      // --- Step 1: load cached profile from sessionStorage ---
      // If present, show the app instantly (no Loading spinner) and verify in background.
      let cachedProfile: User | null = null;
      try {
        const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
        if (raw) {
          cachedProfile = JSON.parse(raw) as User;
          profileCache.current.set(cachedProfile.id, cachedProfile);
          persistCurrentUser(cachedProfile);
          setIsLoading(false); // ← instant: user sees the app immediately
          logger.info('Instant load from cache:', cachedProfile.name);
        }
      } catch (e) {
        logger.warn('Could not parse cached profile');
      }

      // --- Step 2: verify the Supabase session (may involve a silent token refresh) ---
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session?.user) {
          // Session gone — clear everything
          logger.info(sessionError ? 'Session error' : 'No active session');
          if (cachedProfile) {
            profileCache.current.clear();
            persistCurrentUser(null);
          }
          setIsLoading(false);
          return;
        }

        // Session valid; fetch a fresh profile in background (silent — no spinner)
        const freshProfile = await fetchUserProfile(session.user.id);
        if (freshProfile) {
          persistCurrentUser(freshProfile);
        } else {
          logger.warn('Profile not found in DB — signing out...');
          await supabase.auth.signOut();
          profileCache.current.clear();
          persistCurrentUser(null);
        }
      } catch (error) {
        logger.error('Session verification error:', error);
      } finally {
        // Make sure Loading is gone even if something threw unexpectedly
        setIsLoading(false);
      }
    };

    // Hard safety net — never blocked beyond 6 s under any circumstance
    const safetyTimer = setTimeout(() => {
      logger.warn('Auth init safety timeout — forcing isLoading=false');
      setIsLoading(false);
    }, 6000);

    initializeAuth().finally(() => clearTimeout(safetyTimer));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== AUTH STATE CHANGE LISTENER ====================
  useEffect(() => {
    if (!isSupabaseConfigured()) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.info('Auth state change:', event);

      if (event === 'PASSWORD_RECOVERY') {
        logger.info('Password recovery detected — showing reset UI');
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_IN' && session?.user) {
        const profile = await fetchUserProfile(session.user.id);
        if (profile) {
          persistCurrentUser(profile);
        }
      } else if (event === 'SIGNED_OUT') {
        logger.info('User signed out');
        persistCurrentUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistCurrentUser]);

  // ==================== REALTIME SUBSCRIPTION ====================
  useEffect(() => {
    if (!isSupabaseConfigured()) return;

    const channelSuffix = Date.now();
    const usersChannel = supabase
      .channel(`pm-users-changes-${channelSuffix}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'users' },
        (payload) => {
          logger.info('Users change:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            const newUser = mapDbUserToAppUser(payload.new);
            setUsers(prev => {
              if (prev.some(u => u.id === newUser.id)) return prev;
              return [...prev, newUser];
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedUser = mapDbUserToAppUser(payload.new);
            setUsers(prev => prev.map(u =>
              u.id === payload.new.id
                ? {
                    ...u,
                    ...updatedUser,
                  }
                : u
            ));
            if (currentUser?.id === updatedUser.id) {
              persistCurrentUser(updatedUser);
            }
          } else if (payload.eventType === 'DELETE') {
            setUsers(prev => prev.filter(u => u.id !== payload.old.id));
            if (currentUser?.id === payload.old.id) {
              persistCurrentUser(null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(usersChannel);
    };
  }, [currentUser?.id, persistCurrentUser]);

  // ==================== LOGIN ====================
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured. Set environment variables.' };
    }

    try {
      logger.info('Attempting login with email:', email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error('Login error:', error);
        return { success: false, error: error.message };
      }

      if (!data.user) {
        return { success: false, error: 'No user data returned' };
      }

      const profile = await fetchUserProfile(data.user.id);
      if (!profile) {
        await supabase.auth.signOut();
        return { success: false, error: 'User profile not found' };
      }

      persistCurrentUser(profile);
      return { success: true };
    } catch (error: any) {
      logger.error('Login exception:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  // ==================== REGISTER ====================
  const register = async (name: string, email: string, password: string, role: string = 'TECHNICIAN', phone: string = ''): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Registration requires Supabase. Running in demo mode.' };
    }
    try {
      const metadata: Record<string, string> = { name, role };
      if (phone) metadata.notification_phone = phone;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });
      if (error) {
        logger.error('Register error:', error);
        return { success: false, error: error.message };
      }
      if (!data.user) {
        return { success: false, error: 'No user returned from signup' };
      }

      // If phone was provided, update the users table directly
      // (handle_new_user trigger creates the row; we patch notification_phone after)
      if (phone && data.user.id) {
        await supabase
          .from('users')
          .update({ notification_phone: phone })
          .eq('id', data.user.id);
      }

      logger.info('User registered:', data.user.id);
      return { success: true };
    } catch (error: any) {
      logger.error('Register exception:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  // ==================== LOGOUT ====================
  const logout = async () => {
    if (isSupabaseConfigured()) {
      await supabase.auth.signOut();
      profileCache.current.clear();
    }
    persistCurrentUser(null);
    logger.info('User logged out');
  };

  const refreshCurrentUserProfile = useCallback(async (): Promise<User | null> => {
    if (!currentUser) return null;
    profileCache.current.delete(currentUser.id);
    const fresh = await fetchUserProfile(currentUser.id);
    if (fresh) {
      persistCurrentUser(fresh);
      setUsers(prev => prev.map(user => user.id === fresh.id ? { ...user, ...fresh } : user));
    }
    return fresh;
  }, [currentUser, persistCurrentUser]);

  const touchCurrentUserProfile = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser || !isSupabaseConfigured()) return { success: true };

    const rpcResult = await supabase.rpc('touch_my_profile');
    if (!rpcResult.error) return { success: true };

    const fallback = await supabase
      .from('users')
      .update({ name: currentUser.name })
      .eq('id', currentUser.id);

    if (fallback.error) {
      logger.error('Error touching current user profile:', fallback.error);
      return { success: false, error: fallback.error.message };
    }

    return { success: true };
  }, [currentUser]);

  const updateCurrentUserProfile = useCallback(async (updates: Pick<User, 'name'>): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) return { success: false, error: 'No authenticated user.' };

    const trimmedName = updates.name.trim();
    if (!trimmedName) {
      return { success: false, error: 'Name is required.' };
    }

    if (!isSupabaseConfigured()) {
      const nextUser = { ...currentUser, name: trimmedName };
      persistCurrentUser(nextUser);
      setUsers(prev => prev.map(user => user.id === nextUser.id ? { ...user, name: trimmedName } : user));
      return { success: true };
    }

    try {
      const rpcResult = await supabase.rpc('update_my_profile', { p_name: trimmedName });
      const fallbackError = rpcResult.error
        ? (await supabase
            .from('users')
            .update({ name: trimmedName })
            .eq('id', currentUser.id)).error
        : null;
      const error = rpcResult.error ? fallbackError : null;

      if (error) {
        logger.error('Error updating current user profile:', error);
        return { success: false, error: error.message };
      }

      const fresh = await refreshCurrentUserProfile();
      if (!fresh) return { success: false, error: 'Unable to refresh profile after update.' };
      return { success: true };
    } catch (error: any) {
      logger.error('Exception updating current user profile:', error);
      return { success: false, error: error.message || 'Unable to update profile.' };
    }
  }, [currentUser, persistCurrentUser, refreshCurrentUserProfile]);

  const updateCurrentUserAvatar = useCallback(async (file: File): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) return { success: false, error: 'No authenticated user.' };

    const validationError = validateProfileAvatar(file);
    if (validationError) {
      return { success: false, error: validationError };
    }

    try {
      const avatarUrl = await uploadProfileAvatar(file, currentUser.id);

      if (!isSupabaseConfigured()) {
        const nextUser = { ...currentUser, avatarUrl };
        persistCurrentUser(nextUser);
        setUsers(prev => prev.map(user => user.id === nextUser.id ? { ...user, avatarUrl } : user));
        return { success: true };
      }

      const touchResult = await touchCurrentUserProfile();

      if (!touchResult.success) {
        return touchResult;
      }

      const fresh = await refreshCurrentUserProfile();
      if (!fresh) return { success: false, error: 'Unable to refresh profile after avatar upload.' };
      return { success: true };
    } catch (error: any) {
      logger.error('Exception uploading current user avatar:', error);
      return { success: false, error: error.message || 'Unable to upload profile photo.' };
    }
  }, [currentUser, persistCurrentUser, refreshCurrentUserProfile, touchCurrentUserProfile]);

  const removeCurrentUserAvatar = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) return { success: false, error: 'No authenticated user.' };

    try {
      await removeProfileAvatar(currentUser.id);

      if (!isSupabaseConfigured()) {
        const nextUser = { ...currentUser, avatarUrl: undefined };
        persistCurrentUser(nextUser);
        setUsers(prev => prev.map(user => user.id === nextUser.id ? { ...user, avatarUrl: undefined } : user));
        return { success: true };
      }

      const touchResult = await touchCurrentUserProfile();
      if (!touchResult.success) {
        return touchResult;
      }

      const fresh = await refreshCurrentUserProfile();
      if (!fresh) return { success: false, error: 'Unable to refresh profile after avatar removal.' };
      return { success: true };
    } catch (error: any) {
      logger.error('Exception removing current user avatar:', error);
      return { success: false, error: error.message || 'Unable to remove profile photo.' };
    }
  }, [currentUser, persistCurrentUser, refreshCurrentUserProfile, touchCurrentUserProfile]);

  // ==================== USER MANAGEMENT ====================
  const addUser = useCallback(async (user: Omit<User, 'id'> & { password: string }): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      logger.warn('Cannot add user: Supabase not configured');
      return { success: false, error: 'User creation requires Supabase.' };
    }

    try {
      // Runs in a SECURITY DEFINER Postgres function (public.admin_create_user)
      // which validates the caller is a PROJECT_MANAGER, inserts into auth.users
      // (a trigger creates the public.users row), and returns { success, userId }
      // or { error }.
      const { data, error } = await supabase.rpc('admin_create_user', {
        p_name: user.name,
        p_email: user.email,
        p_password: user.password,
        p_role: user.role,
        p_notification_phone: user.notificationPhone || null,
        p_notification_email: user.notificationEmail || null,
      });

      if (error) {
        logger.error('Error creating user (rpc):', error);
        return { success: false, error: error.message };
      }
      const result = data as { success?: boolean; error?: string; userId?: string } | null;
      if (result?.error) {
        logger.error('Error creating user (returned):', result.error);
        return { success: false, error: result.error };
      }

      await fetchUsers();
      logger.info('User created successfully:', user.email);
      return { success: true };
    } catch (error: any) {
      logger.error('Exception creating user:', error);
      return { success: false, error: error.message || 'Error creating user.' };
    }
  }, [fetchUsers]);

  const updateUser = useCallback(async (id: string, updates: Partial<User>) => {
    if (!isSupabaseConfigured()) {
      logger.warn('Cannot update user: Supabase not configured');
      return;
    }

    try {
      logger.info('Updating user:', id);

      const updateData: any = {};
      if (updates.name) updateData.name = updates.name;
      if (updates.role) updateData.role = updates.role;
      if (updates.notificationEmail !== undefined) updateData.notification_email = updates.notificationEmail || null;
      if (updates.notificationPhone !== undefined) updateData.notification_phone = updates.notificationPhone || null;

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', id);

      if (error) {
        logger.error('Error updating user:', error);
        notifyAppError(`Error updating user: ${error.message}`);
        return;
      }

      // Update local state
      setUsers(prev => prev.map(u =>
        u.id === id
          ? {
              ...u,
              ...updates,
              allowedSections: updates.role ? getAllowedSections(updates.role) : u.allowedSections,
              password: '',
            }
          : u
      ));

      if (currentUser?.id === id) {
        const nextUser = {
          ...currentUser,
          ...updates,
          allowedSections: updates.role ? getAllowedSections(updates.role) : currentUser.allowedSections,
        };
        persistCurrentUser(nextUser);
      }

      logger.info('User updated successfully');
    } catch (error: any) {
      logger.error('Exception updating user:', error);
      notifyAppError(`Error updating user: ${error.message}`);
    }
  }, [currentUser, persistCurrentUser]);

  const deleteUser = useCallback(async (id: string) => {
    if (!isSupabaseConfigured()) {
      logger.warn('Cannot delete user: Supabase not configured');
      return;
    }

    try {
      const { error } = await supabase.rpc('delete_auth_user', { target_user_id: id });

      if (error) {
        logger.error('Error deleting user:', error);
        notifyAppError(`Error deleting user: ${error.message}`);
        return;
      }

      // Realtime will update the users list automatically via DELETE event
      logger.info('User deleted successfully:', id);
    } catch (error: any) {
      logger.error('Exception deleting user:', error);
      notifyAppError(`Error deleting user: ${error.message}`);
    }
  }, []);

  // ==================== UPDATE PASSWORD ====================
  const updatePassword = async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Password update not available in demo mode' };
    }
    try {
      // updateUser needs a live auth session. If the token was lost/expired the
      // call fails with a confusing message, so check first and say so plainly.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        logger.error('Password update: no active session');
        return { success: false, error: 'Your session has expired. Please sign in again and retry.' };
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        logger.error('Password update error:', error);
        return { success: false, error: error.message };
      }
      logger.info('Password updated successfully');
      return { success: true };
    } catch (error: any) {
      logger.error('Password update exception:', error);
      return { success: false, error: error.message || 'Password update failed' };
    }
  };

  const completePasswordRecovery = useCallback(() => {
    setIsPasswordRecovery(false);
  }, []);

  // ==================== ADMIN RESET PASSWORD ====================
  const adminResetPassword = async (userEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'No disponible en modo demo' };
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        logger.error('adminResetPassword error:', error);
        return { success: false, error: error.message };
      }
      logger.info('Password reset email sent to:', userEmail);
      return { success: true };
    } catch (err: any) {
      logger.error('adminResetPassword exception:', err);
      return { success: false, error: err.message || 'Reset failed' };
    }
  };

  // ==================== ADMIN SET PASSWORD ====================
  const adminSetPassword = async (userId: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!isSupabaseConfigured()) {
      logger.info('[AUTH] adminSetPassword (mock mode): userId=', userId.slice(0, 8));
      return { success: true };
    }
    try {
      // Admin password changes run in a SECURITY DEFINER Postgres function
      // (public.admin_set_password) which validates the caller is a
      // PROJECT_MANAGER and updates the bcrypt hash in auth.users. Returns
      // { success: true } or { error: '...' }.
      const { data, error } = await supabase.rpc('admin_set_password', {
        target_user_id: userId,
        new_password: newPassword,
      });
      if (error) {
        logger.error('[AUTH] adminSetPassword rpc error:', error);
        return { success: false, error: error.message };
      }
      const result = data as { success?: boolean; error?: string } | null;
      if (result?.error) {
        logger.error('[AUTH] adminSetPassword returned error:', result.error);
        return { success: false, error: result.error };
      }
      logger.info('[AUTH] adminSetPassword: password updated for userId=', userId.slice(0, 8));
      return { success: true };
    } catch (err: any) {
      logger.error('[AUTH] adminSetPassword exception:', err);
      return { success: false, error: err.message || 'Error al actualizar la contraseña.' };
    }
  };

  // ==================== PERMISSIONS ====================
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!currentUser) return false;
    return (RolePermissions[currentUser.role] || []).includes(permission);
  }, [currentUser]);

  const isProjectManager = currentUser?.role === 'PROJECT_MANAGER';
  const isAdmin = isProjectManager; // Alias: PM = Admin in RAE PM
  const isTechnician = currentUser?.role === 'TECHNICIAN';

  const hasSidebarAccess = useCallback((section: SidebarSection): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'PROJECT_MANAGER') return true;
    return (currentUser.allowedSections || []).includes(section);
  }, [currentUser]);

  const value: AuthContextType = {
    currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    isPasswordRecovery,
    login,
    register,
    logout,
    updatePassword,
    updateCurrentUserProfile,
    updateCurrentUserAvatar,
    removeCurrentUserAvatar,
    completePasswordRecovery,
    hasPermission,
    isProjectManager,
    isAdmin,
    isTechnician,
    hasSidebarAccess,
    users,
    addUser,
    updateUser,
    deleteUser,
    adminResetPassword,
    adminSetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
